import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/services';
import { useToast } from '../components/Toast';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';

const SaasAuditLogs = () => {
  const { error: showError } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    loadLogs();
  }, [currentPage, filters, itemsPerPage]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        ...filters
      };
      // Retirer les filtres vides
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });
      
      const response = await adminAPI.getAuditLogs(params);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      showError('Erreur lors du chargement des logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setCurrentPage(1);
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      login: 'bg-entrea-panel text-purple-800',
      logout: 'bg-gray-100 text-gray-800',
      approve: 'bg-entrea-panel text-entrea-navy',
      reject: 'bg-orange-100 text-orange-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  const renderAuditPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={total}
      itemsPerPage={itemsPerPage}
      onPageChange={setCurrentPage}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setCurrentPage(1);
      }}
    />
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Logs d'audit - Administration SaaS"
        description="Historique complet de toutes les activités du système"
      />

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
            <select
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
            >
              <option value="">Toutes les actions</option>
              <option value="create">Créer</option>
              <option value="update">Modifier</option>
              <option value="delete">Supprimer</option>
              <option value="login">Connexion</option>
              <option value="logout">Déconnexion</option>
              <option value="approve">Approuver</option>
              <option value="reject">Rejeter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de ressource</label>
            <input
              type="text"
              name="resource_type"
              value={filters.resource_type}
              onChange={handleFilterChange}
              placeholder="Ex: member, user, contribution"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de début</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
            />
          </div>
          <div className="md:col-span-3 flex gap-2">
            <button
              onClick={() => {
                setFilters({
                  action: '',
                  resource_type: '',
                  user_id: '',
                  start_date: '',
                  end_date: ''
                });
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Liste des logs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 pb-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Logs d'audit ({total})
            </h3>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 px-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-entrea-royal"></div>
          </div>
        ) : logs.length === 0 ? (
          <p className="text-gray-500 text-center py-8 px-6">Aucun log trouvé</p>
        ) : (
          <>
            {total > 0 && renderAuditPagination('top')}
            <div className="overflow-x-auto px-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Heure</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ressource</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Détails</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.created_at ? new Date(log.created_at).toLocaleString('fr-FR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.user_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.resource_type || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {log.resource_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {log.ip_address || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > 0 && renderAuditPagination('bottom')}
          </>
        )}
      </div>
    </div>
  );
};

export default SaasAuditLogs;

